import { useState } from "react";
import {
  Box,
  Typography,
  Button,
  TextField,
  Stack,
  Paper,
  Alert,
} from "@mui/material";
import { motion } from "framer-motion";

import { validateEmailField } from "../utils/validation";
import { notifyUser } from "../api/notifyApi";

export default function ComingSoon() {
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [loading, setLoading] = useState(false);

  const handleNotify = async () => {
    setError("");
    setSuccess("");

    const validationError = validateEmailField(email);
    if (validationError) {
      setError(validationError);
      return;
    }

    try {
      setLoading(true);
      await notifyUser(email);
      setSuccess("You’ll be notified soon");
      setEmail("");
    } catch (err: any) {
      if (err.status === 409) {
        setError(err.message);
      } else {
        setError(err.message || "Something went wrong");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        backgroundColor: "#f5f6f8",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        px: 2,
      }}
    >
      <Box
        component={motion.div}
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        sx={{ width: "100%", maxWidth: 900 }}
      >
        <Paper
          elevation={3}
          sx={{
            p: { xs: 3, md: 6 },
            borderRadius: 3,
            backgroundColor: "#ffffff",
          }}
        >
          <Stack spacing={4} alignItems="center" textAlign="center">
            {/* Title */}
            <Typography variant="h4" fontWeight="bold">
              Flowlink is Coming Soon
            </Typography>

            {/* Subtitle */}
            <Typography color="text.secondary" maxWidth={600}>
              We’re building a powerful feature to help you connect and manage
              your workflows seamlessly. Stay tuned — something exciting is on
              the way.
            </Typography>

            {/* Feature Preview */}
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={2}
              width="100%"
              justifyContent="center"
            >
              <Paper sx={{ p: 2, flex: 1, borderRadius: 2 }}>
                ⚡ Faster Workflows
              </Paper>

              <Paper sx={{ p: 2, flex: 1, borderRadius: 2 }}>
                🔗 Smart Integrations
              </Paper>
            </Stack>

            {/* Email Capture */}
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={2}
              width="100%"
              maxWidth={500}
            >
              <TextField
                fullWidth
                placeholder="Enter your email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                error={!!error}
                helperText={error}
              />

              <Button
                variant="contained"
                onClick={handleNotify}
                disabled={loading}
                sx={{
                  backgroundColor: "#fbbf24",
                  color: "#000",
                  fontWeight: "bold",
                  height: "56px",
                  minWidth: "110px",
                  px: 2,

                  "&:hover": {
                    backgroundColor: "#f59e0b",
                  },
                }}
              >
                {loading ? "Sending..." : "Notify"}
              </Button>
            </Stack>

            {/* Success Message */}
            {success && <Alert severity="success">{success}</Alert>}

            {/* Actions */}
            <Stack direction="row" spacing={2}>
              <Button
                variant="outlined"
                onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
              >
                Back to Top
              </Button>

              <Button
                variant="contained"
                onClick={() => (window.location.href = "/")}
                sx={{
                  backgroundColor: "#fbbf24",
                  color: "#000",
                  fontWeight: "bold",
                  "&:hover": {
                    backgroundColor: "#f59e0b",
                  },
                }}
              >
                Go Home
              </Button>
            </Stack>
          </Stack>
        </Paper>
      </Box>
    </Box>
  );
}
